'use client';

import { useState } from 'react';
import { CheckCircle, Loader2, AlertCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LinkedInPost } from '@/components/momento/social-posts/linkedin-post';
import { XPost } from '@/components/momento/social-posts/x-post';
import { InstagramPost } from '@/components/momento/social-posts/instagram-post';
import { SocialPostActions } from '@/components/momento/social-posts/social-post-actions';

interface WorkflowChoice {
  id: string;
  label: string;
  description: string;
  effort: string;
}

interface WorkflowSuspendData {
  reason: string;
  options: WorkflowChoice[];
}

/**
 * Tool UI for Workflow Suspend States
 * Displays interactive choice cards when workflows pause for user decisions
 *
 * Used by: createContentWorkflow, discoverContentWorkflow
 */
export function WorkflowChoiceToolUI({ part, status }: any) {
  const result = part.result;
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);

  // Loading state
  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Analyzing...
        </span>
      </div>
    );
  }

  // Error state
  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Workflow error
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
          {result.blockers && result.blockers.length > 0 && (
            <ul className="text-xs text-red-700 dark:text-red-300 mt-1 list-disc list-inside">
              {result.blockers.map((blocker: string, i: number) => (
                <li key={i}>{blocker}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Completed state (not suspended)
  if (!result.suspended) {
    // If we have generated content, show a social post preview
    if (result.generatedContent) {
      const contentType = (result.contentType || 'linkedin_post').toLowerCase();
      const platform: 'linkedin' | 'twitter' | 'instagram' =
        contentType.includes('twitter') || contentType.includes('x_') ? 'twitter' :
        contentType.includes('instagram') ? 'instagram' : 'linkedin';

      const PostComponent = {
        linkedin: LinkedInPost,
        twitter: XPost,
        instagram: InstagramPost,
      }[platform];

      return (
        <div className="my-4 max-w-md mx-auto">
          <PostComponent
            authorName="Your Brand"
            content={result.generatedContent}
          />
          <SocialPostActions
            platform={platform}
            caption={result.generatedContent}
          />
          {result.qualityScore && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Quality score: {Math.round(result.qualityScore * 100)}%
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <span className="text-sm font-medium text-green-900 dark:text-green-100">
          Workflow completed
        </span>
      </div>
    );
  }

  // Suspended state - show choice card
  const suspendData = result.suspendData as WorkflowSuspendData;
  const options = suspendData?.options || [];

  if (!suspendData || options.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
        <AlertCircle className="w-4 h-4 text-yellow-600" />
        <span className="text-sm text-yellow-900 dark:text-yellow-100">
          Workflow paused (invalid suspend data)
        </span>
      </div>
    );
  }

  const handleResume = async (choiceId: string) => {
    setResuming(true);
    try {
      const response = await fetch('/api/workflows/resume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workflowId: result.workflowId,
          runId: result.runId,
          resumeData: { choice: choiceId }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[WorkflowChoice] Resume failed:', error);
        throw new Error(error.error || 'Resume failed');
      }

      // Trigger a custom event to notify the assistant-ui runtime
      // This will cause the agent to continue with the workflow result
      window.dispatchEvent(new CustomEvent('workflow-resumed', {
        detail: {
          runId: result.runId,
          choiceId
        }
      }));
    } catch (error) {
      console.error('[WorkflowChoice] Resume error:', error);
      // TODO: Show error message to user
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="rounded-lg bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {suspendData.reason}
          </p>
        </div>
      </div>

      {/* Options */}
      <div className="p-2 space-y-1">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => setSelectedChoice(option.id)}
            disabled={resuming}
            className={`w-full flex items-start gap-2 p-3 rounded-lg text-left transition-colors ${
              selectedChoice === option.id
                ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border border-transparent'
            } ${resuming ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {/* Radio button */}
            <div className="flex-shrink-0 mt-0.5">
              {selectedChoice === option.id ? (
                <div className="w-4 h-4 rounded-full bg-purple-600 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
              )}
            </div>

            {/* Option content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {option.label}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {option.description}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                  {option.effort}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Action Button */}
      <div className="p-2 border-t border-gray-200 dark:border-gray-800">
        <Button
          size="sm"
          className="w-full"
          disabled={!selectedChoice || resuming}
          onClick={() => selectedChoice && handleResume(selectedChoice)}
        >
          {resuming ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Continuing workflow...
            </>
          ) : (
            <>
              Continue
              <ChevronRight className="w-3 h-3 ml-1" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Tool UI for Workflow Completion States
 * Shows workflow results when successfully completed
 */
export function WorkflowCompleteToolUI({ part, status }: any) {
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
        <span className="text-xs text-blue-900 dark:text-blue-100">
          Processing...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return null; // Error handled by WorkflowChoiceToolUI
  }

  // Show completion indicator (agent will present the actual content)
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
      <CheckCircle className="w-3 h-3 text-green-600" />
      <span className="text-xs font-medium text-green-900 dark:text-green-100">
        Complete
      </span>
    </div>
  );
}
